import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useSearchParams } from "react-router-dom";
import { Template, TemplateChannel } from "../../types";
import { Button } from "@shared/ui/button";
import {
  Plus,
  Calendar,
  Mail,
  MessageSquare,
  BellRing,
  MessageCircle,
  FileText,
  Eye,
  Edit,
} from "lucide-react";
import { useToggleTemplateStatus } from "../../hooks";
import { Badge } from "@shared/ui/badge";
import { TemplateDetailDialog } from "../details/TemplateDetailDialog";
import { CharacterCounter } from "../characterCounter/CharacterCounter";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import {
  useCreateTemplate,
  useFilterCommunicationTemplates,
  useUpdateTemplate,
} from "../../hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { TemplateFilterPanel } from "../filters/filters";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Create } from "../create/create";
import {logEvent} from '@shared/analytics/analytics';
import { toast } from "@shared/hooks/use-toast";
import { Switch } from "@shared/ui/switch";

interface TemplateFilters {
  page: number;
  limit: number;
  remote_utility_id?: number;
  search_data?: string;
  channelType?: string;
  category?: string;
  status?: "draft" | "published";
  tags?: string;
}

export const TemplatesManager: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null
  );
  const [togglingTemplate, setTogglingTemplate] = useState<number | null>(null);
  const [dialogMode, setDialogMode] = useState("view");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const handleCreateTemplateClose = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["communicationTemplates", "list"],
    });
    setIsCreateDialogOpen(false);
  };

  const toggleTemplateMutation = useToggleTemplateStatus();

  const getFiltersFromUrl = (): TemplateFilters => {
    const page = parseInt(searchParams.get("page") || "1");
    const limit = parseInt(searchParams.get("limit") || "10");

    const urlFilters: TemplateFilters = {
      page,
      limit,
    };

    const queryParam = searchParams.get("query");
    if (queryParam) {
      urlFilters.search_data = queryParam;
    }

    const channelParam = searchParams.get("channel");
    if (channelParam) {
      urlFilters.channelType = channelParam;
    }

    const categoryParam = searchParams.get("category");
    if (categoryParam) {
      urlFilters.category = categoryParam;
    }

    const statusParam = searchParams.get("status");
    if (
      statusParam &&
      (statusParam === "draft" || statusParam === "published")
    ) {
      urlFilters.status = statusParam;
    }

    const tagsParam = searchParams.get("tags");
    if (tagsParam) {
      urlFilters.tags = tagsParam;
    }

    return urlFilters;
  };

  const [filters, setFilters] = useState<TemplateFilters>(getFiltersFromUrl);

  useEffect(() => {
    const newFilters = getFiltersFromUrl();
    setFilters(newFilters);
  }, [searchParams]);

  const {
    data: templates,
    isLoading: loading,
    error: queryError,
    isFetching,
  } = useFilterCommunicationTemplates(filters);

  const { mutateAsync: createTemplate, isPending: isCreatingTemplate } =
    useCreateTemplate();

  const { mutate: updateTemplate, isPending: isUpdatingTemplate } =
    useUpdateTemplate();

  const getChannelIcon = (channel: TemplateChannel) => {
    switch (channel) {
      case "EMAIL":
        return <Mail className="h-4 w-4" />;
      case "TEXT":
        return <MessageSquare className="h-4 w-4" />;
      case "NOTIFICATION":
        return <BellRing className="h-4 w-4" />;
      case "WHATSAPP":
        return <MessageCircle className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const handleCreateTemplate = (
    newTemplate: Template,
    isDraft: boolean = false
  ) => {
    createTemplate(
      {
        template: newTemplate,
        isDraft: isDraft,
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ["communicationTemplates", "list"],
          });
          setIsCreateDialogOpen(false);
        },
        onError: (error) => {},
      }
    );
  };

  const handleToggleTemplateStatus = async (template: Template) => {
    setTogglingTemplate(template.id);

    try {
      await toggleTemplateMutation.mutateAsync({
        listId: template.id,
        isActive: !template.isActive, // Assuming templates have isActive property
      });

      toast({
        title: "Success",
        description: `Template ${
          !template.isActive ? "activated" : "deactivated"
        } successfully`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update template status",
        variant: "destructive",
      });
    } finally {
      setTogglingTemplate(null);
    }
  };

  const handleUpdateTemplate = (updatedTemplate: Template) => {
    updateTemplate(updatedTemplate, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey;
            return (
              Array.isArray(key) &&
              key[0] === "communicationTemplates" &&
              key[1] === "list"
            );
          },
        });
        setIsCreateDialogOpen(false);
      },
      onError: (error) => {},
    });
    setSelectedTemplate(null);
  };

  const handleApplyFilters = (newFilters: Partial<TemplateFilters>) => {
    const params = new URLSearchParams(searchParams);

    params.delete("channel");
    params.delete("category");
    params.delete("status");
    params.delete("tags");
    params.delete("page");

    if (newFilters.channelType) {
      params.set("channel", newFilters.channelType);
    }
    if (newFilters.category) {
      params.set("category", newFilters.category);
    }
    if (newFilters.status) {
      params.set("status", newFilters.status);
    }
    if (newFilters.tags) {
      params.set("tags", newFilters.tags);
    }

    params.set("page", "1");
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);

    params.delete("channel");
    params.delete("category");
    params.delete("status");
    params.delete("tags");
    params.delete("page");
    params.delete("query");

    setSearchParams(params);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  const handleSearch = (searchTerm: string, paramName: string) => {
    const params = new URLSearchParams(searchParams);

    if (searchTerm.trim()) {
      params.set(paramName, searchTerm);
    } else {
      params.delete(paramName);
    }

    params.set("page", "1");
    setSearchParams(params);
  };

  const columns: TableColumn<Template>[] = [
    {
      key: "templateCategory",
      header: "Category",
      width: "w-32",
      type: "custom",
      render: (_, row) => (
        <Badge variant="outline" className="text-xs">
          {row.templateCategory}
        </Badge>
      ),
    },
    {
      key: "channelType",
      header: "Channel",
      width: "w-24",
      type: "custom",
      render: (_, row) => (
        <Badge variant="secondary" className="flex items-center gap-1 w-fit">
          {getChannelIcon(row.channelType)}
          <span className="capitalize">{row.channelType}</span>
        </Badge>
      ),
    },
    {
      key: "templateName",
      header: "Template Name",
      width: "w-48",
      type: "custom",
      render: (_, row) => (
        <div className="space-y-1">
          <div className="font-medium">{row.templateName}</div>
          <div className="flex flex-col gap-1 mt-1">
            {row.isDefault && (
              <Badge variant="secondary" className="text-xs w-fit">
                Default
              </Badge>
            )}
            {row.isDraft === true && (
              <Badge variant="outline" className="text-xs w-fit">
                Draft
              </Badge>
            )}
            {!row.isDefault && row.isDraft === false && (
              <Badge variant="default" className="text-xs w-fit">
                Published
              </Badge>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "description",
      header: "Description",
      width: "w-64",
      type: "custom",
      render: (_, row) => (
        <div className="text-sm text-muted-foreground line-clamp-2">
          {row.description}
        </div>
      ),
    },
    {
      key: "tags",
      header: "Tags",
      width: "w-32",
      type: "custom",
      render: (_, row) => (
        <div className="flex flex-wrap gap-1">
          {row.tags.slice(0, 2).map((tag) => (
            <Badge key={tag} variant="secondary" className="text-xs">
              {tag}
            </Badge>
          ))}
          {row.tags.length > 2 && (
            <span title={row.tags.slice(2).join(", ")}>
              <Badge variant="secondary" className="text-xs">
                +{row.tags.length - 2}
              </Badge>
            </span>
          )}
        </div>
      ),
    },
    {
      key: "createdBy",
      header: "Created By",
      width: "w-32",
      type: "custom",
      render: (_, row) => (
        <div className="text-sm">
          <div>{row.createdUserRemoteName || "System"}</div>
          <div className="text-xs text-muted-foreground">{row.createdDate}</div>
        </div>
      ),
    },
    {
      key: "updatedBy",
      header: "Updated By",
      width: "w-32",
      type: "custom",
      render: (_, row) => (
        <div className="text-sm">
          <div>{row.lastModifiedUserRemoteName || "System"}</div>
          <div className="text-xs text-muted-foreground">
            {row.lastModifiedDate}
          </div>
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      width: "w-32",
      type: "custom",
      sortable: false,
      render: (_, row) => (
        <div className="flex gap-2 items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              logEvent(`Template - ${row.id} Viewed`);
              setDialogMode("view");
              setSelectedTemplate(row);
            }}
            className="flex items-center gap-1"
          >
            <Eye className="h-3 w-3" />
            View
          </Button>
          {!row.isDefault && (
            <Button
              variant="outline"
              size="sm"
              disabled={row.isDraft == false}
              onClick={() => {
                logEvent(`Template - ${row.id} Edit`);
                setDialogMode("edit");
                setSelectedTemplate(row);
              }}
              className="flex items-center gap-1"
            >
              <Edit className="h-3 w-3" />
              Edit
            </Button>
          )}
          {!row.isDefault && (
            <Switch
              checked={row.isActive}
              onCheckedChange={(checked) => {
                // If trying to deactivate (checked = false) and template is in use
                if (!checked && row.usageCount > 0) {
                  toast({
                    title: "Cannot Deactivate Template",
                    description:
                      "This template is currently in use and cannot be deactivated.",
                    variant: "destructive",
                  });
                  return; // Prevent the toggle action
                }

                // Otherwise, proceed with the toggle
                handleToggleTemplateStatus(row);
              }}
              disabled={togglingTemplate === row.id || row.isDraft == true}
            />
          )}
        </div>
      ),
    },
  ];

  const currentPage = parseInt(searchParams.get("page") || "1");
  const totalPages = templates ? Math.ceil(templates.count / filters.limit) : 1;

  return (
    <div className="space-y-4">
      {/* Loading indicator */}
      {(loading || isFetching || isCreatingTemplate) && (
        <div className="flex justify-center items-center py-8">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
            <span className="text-sm text-gray-600">Loading templates...</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="page-title">
              Communication Templates
            </h2>
            <p className="page-subtitle">
              Manage all communication templates for your organization
            </p>
          </div>
          <Button
            onClick={() => {setIsCreateDialogOpen(true); logEvent('Clicked New Template')}}
            className="flex gap-2"
            disabled={isCreatingTemplate}
          >
            {isCreatingTemplate ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            New Template
          </Button>
        </div>
      </div>

      {/* Generic Table */}
      <GenericTable
        data={templates?.results || []}
        columns={columns}
        loading={loading}
        searchPlaceholder="Search templates..."
        searchParamName="query"
        searchDebounceMs={300}
        onSearch={handleSearch}
        rowKey="id"
        pagination={{
          currentPage: currentPage,
          totalPages: totalPages,
          totalItems: templates?.count || 0,
          itemsPerPage: filters.limit,
          startItem: (currentPage - 1) * filters.limit + 1,
          endItem: Math.min(currentPage * filters.limit, templates?.count || 0),
        }}
        onPageChange={handlePageChange}
        filterPanel={
          <TemplateFilterPanel
            currentFilters={filters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
          />
        }
        emptyMessage="No templates found"
      />

      {/* Dialogs */}
      {selectedTemplate && (
        <TemplateDetailDialog
          template={selectedTemplate}
          open={!!selectedTemplate}
          mode={dialogMode} // Pass the mode
          onClose={() => {
            setSelectedTemplate(null);
            setDialogMode("view"); // Reset to view mode
          }}
          onUpdate={handleUpdateTemplate}
          isUpdating={isUpdatingTemplate}
        />
      )}

      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setIsCreateDialogOpen(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader className="sr-only">
            <DialogTitle>Create New Template</DialogTitle>
          </DialogHeader>

          <Create onClose={handleCreateTemplateClose} mode="create" />
        </DialogContent>
      </Dialog>
    </div>
  );
};
